import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageCircle, X, Send, MapPin, Clock, ShieldAlert, Siren } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ChatFlotante() {
    const [sala, setSala] = useState(null);
    const [messages, setMessages] = useState([]);
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const [unread, setUnread] = useState(0);
    const messagesEndRef = useRef(null);
    const lastMsgRef = useRef('');
    const dismissedRoomRef = useRef(null); // Track dismissed room
    const firstLoadRef = useRef(true);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const userId = (() => { try { return JSON.parse(atob(token?.split('.')[1] || 'e30='))?.sub || ''; } catch { return ''; } })();

    // Verificar si hay sala activa
    const checkRoom = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/chat/active`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data && data.id) {
                    // Solo abrir automáticamente si es una sala NUEVA (no la que el usuario cerró)
                    if (!sala || sala.id !== data.id) {
                        setSala(data);
                        setMessages([]);
                        lastMsgRef.current = '';
                        // Abrir solo si es sala nueva y no fue cerrada por el usuario
                        if (dismissedRoomRef.current !== data.id) {
                            if (firstLoadRef.current) {
                                // Primera carga: no abrir automáticamente
                                firstLoadRef.current = false;
                            } else {
                                setOpen(true);
                            }
                        }
                    } else {
                        setSala(data); // Actualizar datos (ej: expires_at)
                    }
                } else {
                    if (sala) {
                        setSala(null);
                        setOpen(false);
                        dismissedRoomRef.current = null;
                    }
                }
            }
        } catch { /* ignore */ }
    }, [sala]);

    useEffect(() => {
        checkRoom();
        const interval = setInterval(checkRoom, 5000);
        return () => clearInterval(interval);
    }, []);

    // Polling mensajes cada 2s cuando hay sala
    useEffect(() => {
        if (!sala) return;
        const loadMsgs = async () => {
            try {
                const url = lastMsgRef.current
                    ? `${API_URL}/api/alarmas/chat/rooms/${sala.id}/messages?after=${encodeURIComponent(lastMsgRef.current)}`
                    : `${API_URL}/api/alarmas/chat/rooms/${sala.id}/messages`;
                const res = await fetch(url, { headers });
                if (res.ok) {
                    const newMsgs = await res.json();
                    if (newMsgs.length > 0) {
                        if (lastMsgRef.current) {
                            setMessages(prev => [...prev, ...newMsgs]);
                            if (!open) setUnread(prev => prev + newMsgs.length);
                        } else {
                            setMessages(newMsgs);
                        }
                        lastMsgRef.current = newMsgs[newMsgs.length - 1].created_at;
                    }
                }
            } catch { /* ignore */ }
        };
        loadMsgs();
        const interval = setInterval(loadMsgs, 2000);
        return () => clearInterval(interval);
    }, [sala?.id]);

    // Countdown
    useEffect(() => {
        if (!sala?.expires_at) return;
        const tick = () => {
            const now = new Date();
            const exp = new Date(sala.expires_at);
            const diff = Math.max(0, Math.floor((exp - now) / 1000));
            if (diff <= 0) {
                setTimeLeft('Expirado');
                setSala(null);
                setOpen(false);
                return;
            }
            const min = Math.floor(diff / 60);
            const sec = diff % 60;
            setTimeLeft(`${min}:${sec.toString().padStart(2, '0')}`);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [sala?.expires_at]);

    // Auto-scroll
    useEffect(() => {
        if (open && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, open]);

    const handleSend = async () => {
        if (!input.trim() || !sala || sending) return;
        setSending(true);
        const msg = input.trim();
        setInput('');
        try {
            await fetch(`${API_URL}/api/alarmas/chat/rooms/${sala.id}/messages`, {
                method: 'POST', headers,
                body: JSON.stringify({ mensaje: msg }),
            });
        } catch { /* ignore */ }
        finally { setSending(false); }
    };

    const handleOpen = () => {
        setOpen(true);
        setUnread(0);
    };

    const handleClose = () => {
        setOpen(false);
        // Recordar que el usuario cerró esta sala
        if (sala) dismissedRoomRef.current = sala.id;
    };

    if (!sala) return null;

    const tipoIcon = sala.evento_tipo === 'sos'
        ? <ShieldAlert className="h-4 w-4 text-red-500" />
        : <Siren className="h-4 w-4 text-amber-500" />;
    const tipoLabel = sala.evento_tipo === 'sos' ? 'SOS' : sala.evento_tipo === 'test' ? 'PRUEBA' : 'Alerta';

    // Burbuja minimizada
    if (!open) {
        return (
            <button
                onClick={handleOpen}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-red-500 text-white shadow-xl shadow-red-500/30 flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                data-testid="chat-bubble"
            >
                <MessageCircle className="h-6 w-6" />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unread}
                    </span>
                )}
            </button>
        );
    }

    return (
        <div className="fixed bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] bg-card border-2 border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '480px' }} data-testid="chat-panel">
            <div className="flex items-center justify-between px-4 py-3 bg-red-500 text-white">
                <div className="flex items-center gap-2 min-w-0">
                    {tipoIcon}
                    <div className="min-w-0">
                        <p className="font-bold text-sm truncate">{tipoLabel} - {sala.evento_usuario_nombre}</p>
                        <p className="text-xs text-white/70">{sala.participantes?.length || 0} participantes</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5">
                        <Clock className="h-3 w-3" />
                        <span className="text-xs font-mono font-bold">{timeLeft}</span>
                    </div>
                    <button onClick={handleClose} className="p-1 hover:bg-white/20 rounded">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {sala.lat && sala.lng && (
                <a href={`https://www.google.com/maps?q=${sala.lat},${sala.lng}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-4 py-1.5 bg-blue-500/10 text-blue-600 text-xs hover:bg-blue-500/20 transition-colors">
                    <MapPin className="h-3 w-3" /> Ver ubicacion del evento
                </a>
            )}

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                <div className="text-center text-xs text-muted-foreground py-2">
                    {sala.evento_usuario_nombre} activo {tipoLabel.toLowerCase()}
                </div>
                {messages.map((m) => {
                    const isMe = m.usuario_id === userId;
                    return (
                        <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${isMe ? 'bg-red-500 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                                {!isMe && <p className="text-xs font-bold mb-0.5 opacity-70">{m.usuario_nombre}</p>}
                                <p className="break-words">{m.mensaje}</p>
                                <p className={`text-[10px] mt-0.5 ${isMe ? 'text-white/60' : 'text-muted-foreground'}`}>
                                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <div className="px-3 py-2 border-t">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                    <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribe un mensaje..." className="flex-1 h-9 text-sm rounded-full" maxLength={500} data-testid="chat-input" />
                    <Button type="submit" size="icon" disabled={!input.trim() || sending} className="h-9 w-9 rounded-full bg-red-500 hover:bg-red-600">
                        <Send className="h-4 w-4" />
                    </Button>
                </form>
            </div>
        </div>
    );
}
